import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Shield, Heart, Zap, Users, Clock, Target,
  CheckCircle, ArrowRight, Star, Award, 
  IndianRupee, Headphones, Lock
} from 'lucide-react';
import { Button } from '../components/ui/button';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const differentiators = [
  {
    icon: Target,
    title: 'Built for Indian Creators',
    description: 'We understand the unique challenges faced by Indian influencers - from regional trolling to platform-specific issues on Indian social media.',
  },
  {
    icon: Shield,
    title: 'Monitoring + Insurance',
    description: 'Unlike other tools, we don\'t just alert you. Our insurance backing means you have financial protection when things go wrong.',
  },
  {
    icon: Headphones,
    title: 'Human Support, Not Just Bots',
    description: 'Real people who understand creator problems. Get help in Hindi, English, and other Indian languages.',
  },
  {
    icon: IndianRupee,
    title: 'Affordable for Growing Creators',
    description: 'Starting at ₹2,000/month, protection that\'s accessible even for creators just starting to monetize.',
  },
  {
    icon: Clock,
    title: 'Fast Response Time',
    description: 'Average response time under 2 hours. When your reputation is at stake, speed matters.',
  },
  {
    icon: Lock,
    title: 'Privacy First',
    description: 'Read-only access to your accounts. We never post on your behalf or store sensitive credentials.',
  },
];

const trustSignals = [
  { value: '10,000+', label: 'Creators Protected' },
  { value: '500+', label: 'Threats Neutralized' },
  { value: '< 2 hrs', label: 'Avg Response Time' },
  { value: '4.8/5', label: 'Creator Rating' },
];

const testimonials = [
  {
    quote: "After facing a coordinated hate campaign, Sozialsetu helped me document everything and their insurance covered my legal consultation. Game changer.",
    author: "Priya S.",
    role: "YouTube Creator, 250K subscribers",
  },
  {
    quote: "Found 3 fake accounts impersonating me within the first week. Their team helped get them taken down in 48 hours.",
    author: "Rahul M.",
    role: "Instagram Influencer, 180K followers",
  },
  {
    quote: "Finally, protection designed for Indian creators. The WhatsApp alerts and Hindi support make all the difference.",
    author: "Ananya K.",
    role: "Content Creator, Multi-platform",
  },
];

const comparisonRows = [
  { feature: 'India-focused monitoring', sozialsetu: true, others: false },
  { feature: 'Insurance coverage included', sozialsetu: true, others: false },
  { feature: 'Human support in Indian languages', sozialsetu: true, others: false },
  { feature: 'Starting under ₹2,500/month', sozialsetu: true, others: false },
  { feature: 'Impersonation detection', sozialsetu: true, others: true },
  { feature: 'Real-time alerts', sozialsetu: true, others: true },
  { feature: 'Legal consultation access', sozialsetu: true, others: false },
  { feature: 'Crisis response team', sozialsetu: true, others: false },
];

export const WhySozialsetu = () => {
  return (
    <div className="min-h-screen bg-slate-950 pt-20" data-testid="why-sozialsetu-page">
      {/* Hero */}
      <section className="section-spacing relative">
        <div className="absolute inset-0 hero-glow pointer-events-none" />
        
        <div className="container-custom relative z-10">
          <motion.div 
            className="text-center max-w-4xl mx-auto"
            initial="initial"
            animate="animate"
            variants={fadeInUp}
          >
            <div className="inline-flex items-center gap-2 bg-cyan-400/10 border border-cyan-400/20 rounded-full px-4 py-2 mb-6">
              <Heart className="w-4 h-4 text-cyan-400" />
              <span className="text-cyan-400 text-sm font-medium">Made for Indian Creators</span>
            </div>

            <h1 className="text-hero text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Why Creators <span className="text-cyan-400">Trust</span> Sozialsetu
            </h1>

            <p className="text-body max-w-2xl mx-auto">
              We're not just another monitoring tool. We're your complete digital safety partner - 
              combining technology, human support, and financial protection.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Trust Stats */}
      <section className="pb-20 md:pb-32" data-testid="trust-stats">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {trustSignals.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-8 bg-slate-900/50 border border-slate-800 rounded-2xl"
              >
                <div className="text-4xl md:text-5xl font-bold text-cyan-400 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {stat.value}
                </div>
                <div className="text-slate-400">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Differentiators */}
      <section className="section-spacing bg-slate-900/30" data-testid="differentiators">
        <div className="container-custom">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-section text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              What Makes Us <span className="text-cyan-400">Different</span>
            </h2>
            <p className="text-body">
              Purpose-built protection for India's digital creator economy.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {differentiators.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-base group"
              >
                <div className="w-14 h-14 rounded-2xl bg-cyan-400/10 flex items-center justify-center mb-6 group-hover:bg-cyan-400/20 transition-colors">
                  <item.icon className="w-7 h-7 text-cyan-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {item.title}
                </h3>
                <p className="text-slate-400 leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-spacing bg-slate-950" data-testid="testimonials">
        <div className="container-custom">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 mb-4">
              <Star className="w-5 h-5 text-cyan-400" />
              <span className="text-cyan-400 font-medium">Creator Stories</span>
            </div>
            <h2 className="text-section text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Trusted by <span className="text-cyan-400">Creators</span> Like You
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-base"
              >
                <div className="flex gap-1 mb-4">
                  <Star className="w-5 h-5 text-cyan-400 fill-cyan-400" />
                  <Star className="w-5 h-5 text-cyan-400 fill-cyan-400" />
                  <Star className="w-5 h-5 text-cyan-400 fill-cyan-400" />
                  <Star className="w-5 h-5 text-cyan-400 fill-cyan-400" />
                  <Star className="w-5 h-5 text-cyan-400 fill-cyan-400" />
                </div>
                <p className="text-slate-300 leading-relaxed mb-6 italic">
                  "{testimonial.quote}"
                </p>
                <div className="pt-4 border-t border-slate-800">
                  <p className="font-bold text-white">{testimonial.author}</p>
                  <p className="text-sm text-slate-500">{testimonial.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="section-spacing bg-slate-900/30" data-testid="comparison">
        <div className="container-custom">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-section text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Sozialsetu vs <span className="text-cyan-400">Others</span>
            </h2>
          </motion.div>

          <motion.div 
            className="max-w-4xl mx-auto overflow-x-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-800">
                  <th className="text-left py-4 px-4 text-slate-400 font-medium">Feature</th>
                  <th className="text-center py-4 px-4">
                    <span className="text-cyan-400 font-bold">Sozialsetu</span>
                  </th>
                  <th className="text-center py-4 px-4 text-slate-500">Generic Tools</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {comparisonRows.map((row, index) => (
                  <tr key={index}>
                    <td className="py-4 px-4 text-slate-300">{row.feature}</td>
                    <td className="text-center py-4 px-4">
                      {row.sozialsetu ? (
                        <CheckCircle className="w-6 h-6 text-cyan-400 mx-auto" />
                      ) : (
                        <span className="text-slate-600">—</span>
                      )}
                    </td>
                    <td className="text-center py-4 px-4">
                      {row.others ? (
                        <CheckCircle className="w-6 h-6 text-slate-600 mx-auto" />
                      ) : (
                        <span className="text-slate-600">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing bg-slate-950 relative" data-testid="why-cta-section">
        <div className="absolute inset-0 hero-glow pointer-events-none" />
        
        <div className="container-custom relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Award className="w-16 h-16 text-cyan-400 mx-auto mb-6" />
            <h2 className="text-section text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Join the <span className="text-cyan-400">Protected</span> Creator Community
            </h2>
            <p className="text-body max-w-2xl mx-auto mb-8">
              Don't wait for a crisis. Start protecting your digital identity today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button 
                  className="bg-cyan-400 text-slate-950 hover:bg-cyan-300 rounded-full px-8 py-6 font-bold text-base transition-all hover:scale-105"
                  data-testid="get-protected-cta"
                >
                  Get Protected Now
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button 
                  variant="outline"
                  className="bg-slate-800/50 text-white border border-slate-700 hover:border-cyan-400/50 hover:bg-slate-800 rounded-full px-8 py-6 font-medium transition-all"
                  data-testid="view-pricing-cta"
                >
                  View Pricing
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
